/**
 * Aprobación HHEE — contadores y formato de horas compartidos entre el listado general
 * (`useOvertimeApproval`, ASIS-5351) y su pantalla de detalle de solo lectura
 * (`useOvertimeApprovalDetail`, ASIS-5355).
 *
 * Extraído para que ambas pantallas compartan EXACTAMENTE el mismo comportamiento de clic en
 * contador en vez de reimplementarlo: seleccionar "Horas calculadas" limpia cualquier filtro y
 * muestra el total real de los cuatro; seleccionar Pendientes/Aprobadas/No aprobadas resalta ese
 * contador y deja los otros tres en cero, porque sin ese filtro puntual no tienen un total propio
 * que mostrar.
 */

import { semanticColors } from '@/assets/styles/colors';
import { OVERTIME_FLOW_PROFILE_CODE } from '@/api/domains/attendance/types/attendanceConfiguration.types';
import type { IndicatorCardVariant } from '@/components/ui/cards';
import {
  EMPTY_HOUR_VALUE,
  type LC_HourValueOut,
  type OvertimeApprovalCounters,
  type OvertimeApprovalStatus,
  type OvertimeCounterKey,
  type OvertimeFlowProfileCode,
} from '@/api/domains/overtime/overtimeApproval.types';

/** Decimal hours are shown with four decimals and a comma separator, e.g. 12,5000. */
const DECIMAL_PLACES = 4;

export type OvertimeHourFormat = 'HH:MM:SS' | 'DECIMAL';

export const EMPTY_OVERTIME_COUNTERS: OvertimeApprovalCounters = {
  calculated: EMPTY_HOUR_VALUE,
  pending: EMPTY_HOUR_VALUE,
  approved: EMPTY_HOUR_VALUE,
  notApproved: EMPTY_HOUR_VALUE,
  justified: EMPTY_HOUR_VALUE,
};

/** Color por estado de fila, compartido por el listado y el detalle (misma columna "Estado"). */
export const OVERTIME_STATUS_COLORS: Record<OvertimeApprovalStatus, string> = {
  Pendiente: semanticColors.status.error,
  Parcial: semanticColors.status.warning,
  Aprobada: semanticColors.status.success,
  // Estado del revisor (ASIS-5358): equivalente a "resuelto" en su mitad del flujo.
  Justificada: semanticColors.status.success,
};

export function formatOvertimeHours(
  value: LC_HourValueOut,
  hourFormat: OvertimeHourFormat,
): string {
  return hourFormat === 'DECIMAL'
    ? value.decimalValue.toFixed(DECIMAL_PLACES).replace('.', ',')
    : value.hhmmss;
}

export interface OvertimeCounterDefinition {
  key: OvertimeCounterKey;
  label: string;
  tooltip: string;
  value: string;
  variant: IndicatorCardVariant;
  isSelected: boolean;
}

/** Variante visual por contador. No depende del perfil: la misma clave se pinta siempre igual. */
const COUNTER_VARIANTS: Record<OvertimeCounterKey, IndicatorCardVariant> = {
  calculated: 'important',
  pending: 'error',
  approved: 'success',
  notApproved: 'alert',
  justified: 'success',
};

/** Orden de despliegue del aprobador: Calculadas, Pendientes, Aprobadas, No aprobadas. */
export const APPROVER_COUNTER_KEYS: readonly OvertimeCounterKey[] = [
  'calculated',
  'pending',
  'approved',
  'notApproved',
];

/**
 * Orden de despliegue del revisor (ASIS-5358): Calculadas, Pendientes, Justificadas. "Justificadas"
 * son las horas con marca del revisor, no las aprobadas, y "No aprobadas" no le aplica: es un dato
 * de la mitad del flujo que él no ejecuta.
 */
export const REVIEWER_COUNTER_KEYS: readonly OvertimeCounterKey[] = [
  'calculated',
  'pending',
  'justified',
];

/**
 * Cantidad de espacios que ocupa la fila de contadores. Los sets más cortos (el del revisor, con
 * tres) no se estiran para llenarla: dejan el hueco al final, de modo que las tarjetas conserven el
 * mismo ancho en ambos perfiles.
 */
export const OVERTIME_COUNTER_SLOTS = APPROVER_COUNTER_KEYS.length;

/**
 * Set de contadores que corresponde al perfil efectivo del usuario. Cualquier perfil que no sea
 * `REVISOR` (incluido el doble perfil, que el backend resuelve como `APROBADOR`, y el `null` de
 * flujo apagado) ve los cuatro del aprobador — el comportamiento histórico.
 */
export function resolveOvertimeCounterKeys(
  viewerFlowProfile: OvertimeFlowProfileCode | null,
): readonly OvertimeCounterKey[] {
  return viewerFlowProfile === OVERTIME_FLOW_PROFILE_CODE.REVIEWER
    ? REVIEWER_COUNTER_KEYS
    : APPROVER_COUNTER_KEYS;
}

export interface BuildOvertimeCounterDefinitionsParams {
  translate: (key: string) => string;
  counterValues: OvertimeApprovalCounters;
  activeCounterFilter: OvertimeCounterKey;
  hourFormat: OvertimeHourFormat;
  /** Set de contadores a mostrar. Por defecto el del aprobador, que es el histórico. */
  counterKeys?: readonly OvertimeCounterKey[];
}

/**
 * Construye las tarjetas de contadores del set indicado. "Horas calculadas" siempre expone su total
 * real; los demás solo lo hacen cuando el filtro activo es "calculated" (sin filtro puntual) o
 * coincide con su propia clave — en cualquier otro caso quedan en cero porque el usuario ya
 * seleccionó un contador distinto y ese subtotal no aplica a la vista filtrada.
 */
export function buildOvertimeCounterDefinitions(
  params: BuildOvertimeCounterDefinitionsParams,
): OvertimeCounterDefinition[] {
  const {
    translate, counterValues, activeCounterFilter, hourFormat,
    counterKeys = APPROVER_COUNTER_KEYS,
  } = params;

  return counterKeys.map((key) => ({
    key,
    label: translate(`attendance:overtimeApproval.counters.${key}.label`),
    tooltip: translate(`attendance:overtimeApproval.counters.${key}.tooltip`),
    value: formatOvertimeHours(
      key === 'calculated' || activeCounterFilter === 'calculated' || activeCounterFilter === key
        ? counterValues[key]
        : EMPTY_HOUR_VALUE,
      hourFormat,
    ),
    variant: COUNTER_VARIANTS[key],
    isSelected: activeCounterFilter === key,
  }));
}
